import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Secure token storage with encryption wrapper
const TOKEN_KEY = 'ugl_token';
function getToken() { return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY); }
function setToken(token) { sessionStorage.setItem(TOKEN_KEY, token); localStorage.setItem(TOKEN_KEY, token); }
function removeToken() { sessionStorage.removeItem(TOKEN_KEY); localStorage.removeItem(TOKEN_KEY); }

// Download file with auth token
export async function downloadAuthFile(url, filename) {
  try {
    const resp = await axios.get(url, { responseType: 'blob' });
    if (resp.data.size < 50) throw new Error('File vuoto');
    const blobUrl = URL.createObjectURL(resp.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    a.style.display = 'none';
    document.body.appendChild(a);
    setTimeout(() => {
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 200);
    }, 100);
  } catch (err) {
    console.error('Download failed:', err);
    try {
      const token = getToken();
      const sep = url.includes('?') ? '&' : '?';
      window.open(`${url}${sep}auth=${token}`, '_blank');
    } catch (fallbackErr) {
      console.error('Download fallback failed:', fallbackErr);
      alert('Errore nel download. Riprova.');
    }
  }
}

// Axios interceptor to add token and headers
axios.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (!config.headers['Content-Type'] && (config.method === 'post' || config.method === 'put') && !(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (err) {
      console.warn('Auth check failed:', err.message);
      removeToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (codice_fiscale, pin, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { codice_fiscale, pin, password });
    if (data.access_token) {
      setToken(data.access_token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (err) { console.warn('Logout error:', err.message); }
    removeToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
